import type { ModelProvider, ModelRequest, ModelResponse, ModelStreamChunk, ModelToolCall } from '@fabric-harness/sdk';

export interface MockDatabricksModelProviderOptions {
  /**
   * Optional prompt-to-response map. Keys are substrings matched against the
   * latest user message. Values are either a plain text response or a
   * `ModelResponse` object.
   */
  responses?: Record<string, string | ModelResponse>;
  /** Optional deterministic tool calls to return when no map entry matches. */
  defaultToolCalls?: ModelToolCall[];
  /** Optional deterministic text response when no map entry matches. */
  defaultText?: string;
}

function estimateUsage(messages: Array<{ content: string }>): { totalTokens: number } {
  const chars = messages.reduce((sum, m) => sum + m.content.length, 0);
  return { totalTokens: Math.ceil(chars / 4) };
}

/**
 * A deterministic `ModelProvider` for Databricks agent tests and init templates.
 * Returns structured responses for SQL/table-info tool calls without requiring
 * real Databricks credentials.
 */
export class MockDatabricksModelProvider implements ModelProvider {
  readonly name = 'mock-databricks';
  private readonly options: MockDatabricksModelProviderOptions;

  constructor(options: MockDatabricksModelProviderOptions = {}) {
    this.options = options;
  }

  async generate(request: ModelRequest): Promise<ModelResponse> {
    const latest = [...request.messages].reverse().find((m) => m.role === 'user');
    const text = latest?.content ?? '';

    // Check prompt-to-response map first
    if (this.options.responses) {
      for (const [key, value] of Object.entries(this.options.responses)) {
        if (text.toLowerCase().includes(key.toLowerCase())) {
          if (typeof value === 'string') {
            return {
              message: { role: 'assistant', content: value },
              usage: estimateUsage([...request.messages, { content: value }]) ?? { totalTokens: 0 },
            };
          }
          return value;
        }
      }
    }

    // Return default tool calls if configured
    if (this.options.defaultToolCalls && this.options.defaultToolCalls.length > 0) {
      return {
        toolCalls: this.options.defaultToolCalls,
        usage: estimateUsage(request.messages) ?? { totalTokens: 0 },
      };
    }

    // Fallback to default text or a generic mock response
    const content = this.options.defaultText ?? `Mock Databricks response: ${text}`;
    return {
      message: { role: 'assistant', content },
      usage: estimateUsage([...request.messages, { content }]) ?? { totalTokens: 0 },
    };
  }

  async *stream(request: ModelRequest): AsyncIterable<ModelStreamChunk> {
    const response = await this.generate(request);
    if (response.message?.content) {
      yield { textDelta: response.message.content };
    }
    if (response.toolCalls) {
      for (const toolCall of response.toolCalls) {
        yield {
          done: {
            toolCalls: [toolCall],
            ...(response.usage !== undefined ? { usage: response.usage } : {}),
          },
        };
        return;
      }
    }
    yield { done: response };
  }
}
